/*
 * Copyright (c) 2022. CodeGen International (Pvt) Ltd. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of CodeGen
 * International (Pvt) Ltd. ("Confidential Information"). You shall not disclose
 * such Confidential Information and shall use it only in accordance with the
 * terms of the license agreement you entered into with CodeGen International.
 */

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSliderChange } from '@angular/material';
import { TcApiError } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-api-error';
import { TcErrorType } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-error';
import { TcHttpError } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-http-error';
import { DialogModel, LEVEL } from '@tc-core/model/it/codegen/ui/framework/dialog-model';
import { SummaryCardData } from '@tc-core/model/it/codegen/ui/framework/summary-card-data';
import { TC } from '@tc-core/util/constants';
import { ConfigLoader } from '@tc-core/util/framework/config-loader.service';
import { EventManager } from '@tc-core/util/framework/event-manager.service';
import { UserJourneyManager } from '@tc-core/util/framework/user-journey-manager.service';
import { CommonHelper } from '@tc-core/util/helpers/common-helper.service';
import { SpinnerService } from '@tc-core/util/ui/spinner.service';
import { DialogService } from '@tc/dialog/dialog.service';
import { Subscription } from 'rxjs';
import { TCO } from '../../constants';
import { ResourceTypeSearchCriteria } from '../../models/criteria/resource-type-search-criteria';
import { AllocationFeature } from '../../models/reservation-v2/allocation-feature';
import { AllocationFeatureSearchCriteria } from '../../models/reservation-v2/criteria/allocation-feature-search-criteria';
import { OPResponseWrapper } from '../../models/response/op-response-wrapper';
import { ReservationV2ManagementService } from '../../services/backend-consumers/reservation-v2/reservation-v2-management.service';
import { ResourceTypeService } from '../../services/backend-consumers/setups/resource-type.service';
import { DMCCommon } from '../../services/util/common/dmc-common';
import { DataKey, DataStoreService } from '../../services/util/framework/data-store.service';
import { ResponseUtil } from '../../services/util/response/response-util.service';
import { DMCLocalStorageService, LocalDataKey } from '../../services/util/system/dmc-local-storage.service';
import { ContentSummaryCardService } from '../../services/util/ui/content-summary-card.service';

@Component({
    selector: 'tc-allocation-feature',
    templateUrl: './allocation-feature.component.html'
})
export class AllocationFeatureComponent implements OnInit, OnDestroy
{
    private featureSearchResultsSubscription: Subscription = new Subscription();
    private createFeatureObserver: Subscription = new Subscription();

    allocationFeatureSearchResults: AllocationFeature[] = [];
    featureShowing: AllocationFeature[] = [];
    allocationFeatureSearchCriteria: AllocationFeatureSearchCriteria = new AllocationFeatureSearchCriteria();

    disabled = false;
    max = 10;
    min = 0;
    step = 1;
    thumbLabel = true;
    vertical = false;
    tickInterval = 1;
    isEnableSaveButton = false;
    isEnableDeleteButton = true;
    serviceTypeList = [];
    filteredServiceTypeList = [];
    selectedSummary: AllocationFeature;
    expandedPanelIndex = -1;
    allocationFeatureConfig: any;

    allResourceType = {
        code: 'All',
        isChecked: false,
        isShow: true,
        name: 'All',
        profileTypes: [],
        resourceCategory: ''
    };
    @Output() select: EventEmitter<AllocationFeature> = new EventEmitter();
    @Output() cardScroll: EventEmitter<number> = new EventEmitter<number>();

    constructor(
        private userJourneyManager: UserJourneyManager,
        private em: EventManager,
        private dataStore: DataStoreService,
        private spinnerService: SpinnerService,
        private reservationV2ManagementService: ReservationV2ManagementService,
        private dialogService: DialogService,
        private commonHelper: CommonHelper,
        private localStorage: DMCLocalStorageService,
        private common: DMCCommon,
        private configLoader: ConfigLoader,
        private summaryCardService: ContentSummaryCardService,
        private resourceTypeService: ResourceTypeService
    )
    { }

    ngOnInit()
    {
        this.allocationFeatureConfig = this.configLoader.configurations.get(TCO.CONF.CONF_ALLOCATION_FEATURE_CONFIG);

        this.subscribeAllocationFeatureCriteria();
        this.getAllocationFeatures();
        this.getServiceType();

        this.em.addEvent(TC.CONF.CONF_EVENT_MANAGER, 'ADD_FEATURES').subscribe(e => {
            this.featureShowing.push(new AllocationFeature());
        });
        this.userJourneyManager.canProceed.next(true);
    }

    /**
     * On each and every change event of an input this getting triggered.
     */
    onInputChange(event: MatSliderChange | undefined, type: string, allocationFeature: AllocationFeature, option?: any)
    {
        switch (type) {
            case 'cost':
                allocationFeature.cost = event.value;
                if (allocationFeature.isServiceTypeValid) { this.isEnableSaveButton = true; }
                break;
            case 'grade':
                allocationFeature.grade = event.value;
                if (allocationFeature.isServiceTypeValid) { this.isEnableSaveButton = true; }
                break;
            case 'preference':
                allocationFeature.preference = event.value;
                if (allocationFeature.isServiceTypeValid) { this.isEnableSaveButton = true; }
                break;
            case 'alphabetical order':
                allocationFeature.alphabeticalOrder = event.value;
                if (allocationFeature.isServiceTypeValid) { this.isEnableSaveButton = true; }
                break;
            case 'serviceType':
                allocationFeature.serviceTypeName = option.name;
                break;
            default:
                break;
        }
    }

    subscribeAllocationFeatureCriteria()
    {
        this.featureSearchResultsSubscription = this.dataStore.get(DataKey.allocationFeatureSearchCriteria, true)
                                                    .subscribe((data: AllocationFeatureSearchCriteria) => {
                                                        if (data) {
                                                            this.allocationFeatureSearchCriteria = data;
                                                            this.featureShowing = [];
                                                        }
                                                    });
    }

    /**
     * Retrieve all the saved allocation features
     */
    getAllocationFeatures()
    {
        this.reservationV2ManagementService.searchFeatureItems(this.allocationFeatureSearchCriteria)
            .subscribe((data: OPResponseWrapper<AllocationFeature>) => {
                if (data) {
                    this.featureShowing = [];
                    this.allocationFeatureSearchResults = ResponseUtil.getDataArray<AllocationFeature>(data);
                    this.featureShowing.push(...this.allocationFeatureSearchResults);
                }
            });
    }

    saveAllocationFeature(allocationFeature: AllocationFeature)
    {
        this.dataStore.set(DataKey.saveAllocationFeature, null, true);
        this.createAllocationFeatureObserver();
        this.setAllocationFeature(allocationFeature);
        this.spinnerService.show();
        this.reservationV2ManagementService.saveAllocationFeature(allocationFeature)
            .subscribe(
                result => {
                    if (result.status.code === TC.ErrorCodes.SUCCESS) {
                        this.getAllocationFeatures();
                    }
                },
                (error) => {
                    this.common.showSnackBar(error, 3000, TcErrorType.TYPE.ERROR);
                },
                () => {
                    this.spinnerService.hide();
                }
            );
    }

    deleteAllocationFeature(allocationFeature: AllocationFeature)
    {
        this.dataStore.set(DataKey.saveAllocationFeature, null, true);
        this.createAllocationFeatureObserver();
        this.spinnerService.show();
        this.reservationV2ManagementService.deleteAllocationFeature(allocationFeature.serviceType)
            .subscribe((result) => {
                    if (result.status.code === TC.ErrorCodes.SUCCESS) {
                        this.getAllocationFeatures();
                    }
                }, (error) => {
                    this.common.showSnackBar(error, 3000, TcErrorType.TYPE.ERROR);
                },
                () => {
                    this.spinnerService.hide();
                }
            );
    }

    /**
     * Set default values as 0.
     */
    setAllocationFeature(allocationFeature: AllocationFeature)
    {
        if (allocationFeature.cost == null) {
            allocationFeature.cost = 0;
        }
        if (allocationFeature.grade == null) {
            allocationFeature.grade = 0;
        }
        if (allocationFeature.preference == null) {
            allocationFeature.preference = 0;
        }
        if (allocationFeature.alphabeticalOrder == null) {
            allocationFeature.alphabeticalOrder = 0;
        }
    }

    createAllocationFeatureObserver()
    {
        this.createFeatureObserver = this.dataStore.get(DataKey.saveAllocationFeature, true).subscribe(result => {
            if (this.commonHelper.dataValidity(result)) {
                this.createFeatureObserver.unsubscribe();
                this.spinnerService.hide();
                this.dataStore.set(DataKey.saveAllocationFeature, null, true);
                if (result.status.code === TC.ErrorCodes.SUCCESS) {
                    this.isEnableSaveButton = false;
                    this.localStorage.delete(LocalDataKey.unSavedFeature);
                    this.common.showSnackBar(
                        'Auto Allocation Feature Saved Successfully.',
                        3000,
                        TcErrorType.TYPE.INFO
                    );
                } else {
                    const confirmError = new DialogModel(
                        true,
                        LEVEL.ERROR,
                        'Failed',
                        'Failed to Save the Auto Allocation Feature Details',
                        true,
                        2000
                    );
                    this.dialogService
                        .confirm(confirmError)
                        .subscribe((res) => {
                            if (res === true) {
                                console.log('true');
                            }
                        });
                }
            } else if (result instanceof TcApiError) {
                this.spinnerService.hide();
            } else if (result instanceof TcHttpError) {
                this.spinnerService.hide();
            }
        });
    }

    getServiceType()
    {
        this.dataStore.get(DataKey.resourceTypes).subscribe(types => {
            if (types) {
                this.serviceTypeList = types;
                this.serviceTypeList.push(this.allResourceType);
                this.filteredServiceTypeList = this.serviceTypeList;
            }
        });
    }

    displayServiceTypeFn(serviceType: any)
    {
        return serviceType && serviceType.name ? serviceType.name : serviceType;
    }

    onServiceTypeFocus()
    {
        this.filteredServiceTypeList = [];
    }

    filterServiceTypes(input: string)
    {
        const resourceTypeSearchCriteria = new ResourceTypeSearchCriteria();
        resourceTypeSearchCriteria.name = input;

        this.resourceTypeService.getResourceTypes(resourceTypeSearchCriteria).subscribe(data => {
            this.filteredServiceTypeList = ResponseUtil.getDataArray(data);
            if ('all'.search(input.toLowerCase()) > -1) {
                this.filteredServiceTypeList.push(this.allResourceType);
            }
        });
        const filteredFeatures = this.featureShowing.filter(feature => feature.serviceType.search(input) > -1);
        let filteredFeature;
        if (filteredFeatures) {
            filteredFeature = filteredFeatures[0];
            const serviceTypesLength = filteredFeature.length;
            if (serviceTypesLength > 1) {
                filteredFeature.serviceTypeError = 'Service type is already exists';
                this.isEnableSaveButton = false;
                this.isEnableDeleteButton = false;
            } else {
                filteredFeature.isServiceTypeValid = true;
                this.isEnableSaveButton = true;
                this.isEnableDeleteButton = true;
            }
        }
    }

    onSelection(summaryCardData: AllocationFeature, index?: number)
    {
        this.selectedSummary = summaryCardData;

        if (summaryCardData) {
            this.select.emit(summaryCardData);
            this.cardScroll.emit(index);
        }
    }

    getSummaryCardData(feature: AllocationFeature): SummaryCardData
    {
        const contentCardList = this.summaryCardService.getContentCardsByConfig(
            this.allocationFeatureConfig,
            [feature]
        );
        return contentCardList[0];
    }

    public onHeaderClick()
    {
        this.selectedSummary = null;
    }

    ngOnDestroy(): void
    {
        if (this.featureSearchResultsSubscription) {
            this.featureSearchResultsSubscription.unsubscribe();
        }
        if (this.createFeatureObserver) {
            this.createFeatureObserver.unsubscribe();
        }
    }
}
